import React, { useMemo } from 'react';
import { isEmpty } from 'validate.js';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'dpl/common/design_system/Button';
import BreederProfileOwnerStoriesTabSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileOwnerStoriesTabSkeleton';
import OwnerStory from 'dpl/components/OwnerStory';
import useInfiniteCollectionQuery from 'dpl/shared/fetching/hooks/useInfiniteCollectionQuery';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';
import { resetParams } from 'dpl/actions/query_params';

import { reviewsTestimonialIndexResourceDefinition } from '../utils/queryDefinitions';
import { BREED_ID, LOCATION } from '../utils/constants';

export const STORIES_PER_PAGE = window.__DPL_BOT_UA ? 20 : 8;

export default function OwnerStoriesSection() {
  const queryParamBreed = useSelector(
    ({ queryParams }) => queryParams[BREED_ID]
  );
  const queryParamLocation = useSelector(
    ({ queryParams }) => queryParams[LOCATION]
  );

  const {
    infiniteScrollItems,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching,
    data
  } = useInfiniteCollectionQuery(
    reviewsTestimonialIndexResourceDefinition({
      pageNumber: 1,
      perPage: STORIES_PER_PAGE,
      breedId: queryParamBreed,
      citySlug: queryParamLocation
    })
  );

  const dispatch = useDispatch();

  const filteredTestimonialsCount =
    data?.pages[0]?.data?.meta?.total_count || 0;

  const columns = useMemo(() => {
    return infiniteScrollItems.reduce(
      (acc, story, index) => {
        const key = isGreaterThanBreakpoint('md') ? index % 2 : 0;
        acc[key].push(story);
        return acc;
      },
      [[], []]
    );
  }, [infiniteScrollItems]);

  if (isLoading) {
    return <BreederProfileOwnerStoriesTabSkeleton />;
  }

  function clearFilters() {
    dispatch(resetParams(BREED_ID, LOCATION, { hash: true }));
  }

  if (!isLoading && infiniteScrollItems.length === 0) {
    return (
      <div className="ReviewsEmptyOwnerStoriesSection container-xxl tc mb6 mb12-lg">
        <h4 className="font-24 mb4">
          No reviews yet for this search criteria.
        </h4>
        <div className="w-100 flex justify-center mb6 mb12-lg">
          <Button
            className="font-16 w-100 w-auto-l"
            onClick={clearFilters}
            size="default"
          >
            Clear filters
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section className="ReviewsOwnerStoriesSection ph5 ph20-lg container-xxl">
      <div className="font-14 lg:font-16 fw-medium">
        Browse {filteredTestimonialsCount.toLocaleString()} owner{' '}
        {filteredTestimonialsCount !== 1 ? 'stories' : 'story'}
      </div>
      <ul className="grid grid-cols-1 lg:grid-cols-2 justify-between gap-24 mb6 mb12-lg">
        {columns
          .filter(column => !isEmpty(column))
          .map((stories, colIndex) => (
            <li key={colIndex}>
              {stories.map(story => (
                <OwnerStory
                  key={story.id}
                  id={story.id}
                  className="ba br4 b--default ph4 pv4 mt6 overflow-hidden"
                  date={story.formatted_submitted_at}
                  description={story.experience}
                  gallery={story.photographs}
                  user={story.buyer_profile}
                  breederProfile={story.breeder_profile}
                  expandInModal
                />
              ))}
            </li>
          ))}
      </ul>
      {hasNextPage && (
        <div className="w-100 flex justify-center mb12">
          <Button
            className="font-16 w-100 w-auto-l"
            onClick={fetchNextPage}
            disabled={isFetching}
            size="default"
          >
            Load more
          </Button>
        </div>
      )}
    </section>
  );
}
